import { notice } from 'feature/notice/NoticeController'
import { try_on } from 'feature/widget/TryOnStore'
import { i18n } from 'i18n'
import { makeAutoObservable, runInAction } from 'mobx'
import { boutique } from 'page/catalog/employee/store/BoutiqueController'
import { employee } from 'page/catalog/employee/store/EmployeeStore'
import { demo } from 'page/catalog/store/CatalogStore'
import { user } from 'page/catalog/store/UserStore'
import { PhoneStore } from 'page/catalog/UserModal/store/PhoneStore'
import { SearchStore } from 'page/catalog/UserModal/store/SearchStore'
import { api } from 'store/api'
import { mx } from 'store/base/mx'
import { Client } from 'type/client'
import { DemoCatalogData } from 'type/demo'
import { Product } from 'type/Product'
import { omit } from 'util/object'

type Step = 'search' | 'create' | 'edit' | 'fail'

export class UserModalStore {
  private readonly product: Product

  readonly company: string
  readonly access_key: string | null
  readonly manager: string | null

  readonly search: SearchStore

  private _name = ''
  readonly id: PhoneStore
  private _phone = ''
  private _email = ''
  private _card = ''

  private _busy = false
  private _error = ''
  private _data: Client | null = null
  private _touch = false
  private _step: Step = 'search'
  private _creating = false
  private _success = ''

  constructor(demo: DemoCatalogData, user?: Client) {
    const company = demo.product.company_id
    this.product = demo.product
    this.company = company
    this.access_key = demo.widget.access || null
    this.manager = employee.manager_id || null
    this.search = new SearchStore(company)
    this.id = new PhoneStore(company)
    this._data = user || null
    makeAutoObservable(this)
  }

  get name(): string {
    return this._name
  }

  set name(value: string) {
    this._name = value
  }

  get phone(): string {
    return this._phone
  }

  set phone(value: string) {
    this._phone = value
  }

  get email(): string {
    return this._email
  }

  set email(value: string) {
    this._email = value
  }

  get card(): string {
    return this._card
  }

  set card(value: string) {
    this._card = value
  }

  get busy(): boolean {
    return this._busy
  }

  get error(): string {
    return this._error
  }

  get touch(): boolean {
    return this._touch
  }

  get data(): Client | null {
    return this._data
  }

  get step(): Step {
    return this._step
  }

  get creating(): boolean {
    return this._creating
  }

  set step(step: Step) {
    this._step = step
    if (step === 'create') {
      this._creating = true
      this.id.change(this.search.text)
      this._name = ''
      this._phone = ''
      this._email = ''
      this._card = ''
    }
    if (step === 'edit') {
      const be = demo.it.by_email
      const bc = demo.it.by_card
      const { name, phone, email, card } = this.data ?? {}
      this._creating = false
      this._name = name || ''
      this.id.change(be ? email : bc ? card : phone)
      this._phone = phone || ''
      this._email = email || ''
      this._card = card || ''
    }
  }

  get success(): string {
    return this._success
  }

  get canCreate(): boolean {
    return this.id.good && !!this.name
  }

  startEdit(data: Client | null) {
    this._data = data
    this.step = 'edit'
  }

  async create(access: string): Promise<void> {
    this._busy = true
    this._error = ''
    try {
      const be = demo.it.by_email
      const bc = demo.it.by_card
      const { company, manager, name, phone, email, card } = this
      const request = { company, manager, name, phone, email, card, access }

      if (be) request.email = this.id.email
      else if (bc) request.card = this.id.card
      else request.phone = this.id.phone

      const data = await api.createUser(request)
      this.sendClientEvent(data)
      runInAction(() => {
        this._busy = false
        this._data = data
        const client = boutique.createClient(data)
        if (client) user.open(client)
        userModal.close()
        notice.success(i18n('CustomerSuccessfullyCreated'))
        try_on.open()
      })
    } catch (e: any) {
      console.error('create failed', e)
      const exists = e.message.includes('user already exists')
      runInAction(() => {
        this._busy = false
        this._step = 'fail'
        if (exists) this._error = i18n('error.UserAlreadyExists')
      })
    }
  }

  async save(): Promise<void> {
    const { user_id } = this.data ?? {}
    if (!user_id) return

    this._busy = true
    this._error = ''
    try {
      const be = demo.it.by_email
      const bc = demo.it.by_card
      const { name, phone, email, card } = this
      const request = { user_id, name, phone, email, card }

      if (be) request.email = this.id.email
      else if (bc) request.card = this.id.card
      else request.phone = this.id.phone

      const data = await api.updateUser(request)
      runInAction(() => {
        this._busy = false
        this._data = data
        const client = boutique.createClient(data)
        if (client) user.open(client)
        userModal.close()
        notice.success(i18n('CustomerDetailsSaved'))
      })
    } catch (e) {
      console.error('update failed', e)
      runInAction(() => {
        this._busy = false
        this._step = 'fail'
      })
    }
  }

  private sendClientEvent(client: Client) {
    const name = 'boutique_client_new'
    const user_id = client.user_id
    const access_key = this.access_key || ''
    const manager_id = this.manager || ''
    const object_id = undefined
    const origin = location.origin
    const { company_id, product_id } = this.product
    const data = omit({ company_id, access_key, origin, product_id, manager_id, boutique: true })
    const event = omit({ name, user_id, object_id, data })
    void api.sendEvent(event)
  }

  logout() {
    this._data = null
    user.close()
  }
}

export const userModal = mx.ref<UserModalStore>()
